import { useMemo } from 'react';

import {
  Card,
  EluveCardContent,
  EluveCardHeader,
  Stack,
  Text,
} from '@eluve/components';
import { useFhirSearch } from '@eluve/fhir-data';
import {
  FhirResourceScope,
  RenderResource,
  buildResolutionMap,
} from '@eluve/fhir-renderer';
import { FHIR_LOCATION_ID_SYSTEM } from '@eluve/fhir-utils';

/**
 * The location's FHIR mirror, read-only, for operators debugging what the
 * mirror actually holds. The resource is written by the `sync_fhir_mirror`
 * job; edits belong in the eluve form above, which the trigger propagates.
 *
 * Keyed by identifier, not by FHIR id: the mirror's durable key is
 * `https://eluve.com/location-id|<eluve location id>` and the FHIR id isn't
 * stored on the eluve row.
 */
const MirrorBody: React.FC<{ locationId: string }> = ({ locationId }) => {
  const { resources, loading, error, enabled } = useFhirSearch('Location', {
    identifier: `${FHIR_LOCATION_ID_SYSTEM}|${locationId}`,
  });
  const map = useMemo(() => buildResolutionMap([]), []);
  const location = resources[0];

  if (!enabled) {
    return (
      <Text size="sm" color="tertiary">
        FHIR session unavailable.
      </Text>
    );
  }

  if (loading) {
    return (
      <Text size="sm" color="tertiary">
        Loading FHIR resource…
      </Text>
    );
  }

  if (error) {
    return (
      <Text size="sm" color="tertiary">
        Couldn't load the FHIR resource.
      </Text>
    );
  }

  if (!location) {
    return (
      <Text size="sm" color="tertiary">
        Not mirrored yet.
      </Text>
    );
  }

  return (
    <Stack gap={2} align="stretch">
      <FhirResourceScope map={map}>
        <RenderResource resource={location} />
      </FhirResourceScope>
      <Text size="xs" color="tertiary" className="font-mono">
        Location/{location.id}
      </Text>
    </Stack>
  );
};

export const LocationFhirMirrorCard: React.FC<{ locationId: string }> = ({
  locationId,
}) => (
  <Card className="w-full rounded-xl border-border-primary">
    <EluveCardHeader>
      <Text>FHIR Mirror</Text>
    </EluveCardHeader>
    <EluveCardContent>
      <MirrorBody locationId={locationId} />
    </EluveCardContent>
  </Card>
);
